import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { CustomerService, CreateCustomerRequest, CreateSiteRequest } from '../../APIService/CustomerService';
import moment from 'moment';

// ─── Shared interfaces used across pages ───────────────────────────────────────

export interface CustomerItem {
    id: string;
    name: string;
    accountNumber: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    sites: number;
    administrator?: string;
    status?: 'Active' | 'Pending' | 'Inactive';
}

export interface SiteItem {
    id: string;
    name: string;
    address: string;
    city: string;
    state: string;
    zipCode?: string;
    country?: string;
    sitesCount: number;
    administrator: string;
    administratorStatus?: string;
    status?: 'Active' | 'Pending' | 'Inactive';
    subscriptionLevel?: number;
}

export interface FilterItem {
    id: string;
    name: string;
    site: string;
    createdOn: string;
    createdBy: string;
    status: 'Active' | 'Inactive' | 'Trial' | 'Expired';
    subscriptionLevel: number;
}

export interface FilterDetailData {
    filter_name: string;
    serial_number: string;
    filter_id: string;
    site_id: string;
    subscription_details: {
        subscription_level: number;
        subscription_description: string;
        start_date: number;
        expiration_date: number;
        issued_date: number;
        is_trail: boolean;
        subscription_remaining_days: number;
        is_expired: boolean;
        subscription_status: number;
    };
    filter_type: number;
    filter_technology: number;
    model_type: number;
    design_pressure_max: number;
    design_temperature_max: number;
    differential_pressure_max: number;
    air_pressure_max: number;
    flowrate_max: number;
    components: any[];
    created_on: number;
    created_by: string;
    updated_on: number;
    updated_by: string;
    is_active: boolean;
}

export interface UserItem {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    site: string;
    status: 'Active' | 'Pending' | 'Inactive';
}

export interface RoleItem {
    id: string;
    name: string;
}

// ─── State shape ────────────────────────────────────────────────────────────────

interface AppState {
    // Lists
    customers: CustomerItem[];
    sites: SiteItem[];
    filters: FilterItem[];
    users: UserItem[];
    roles: RoleItem[];

    // Selections (no IDs in URL — state drives navigation)
    selectedCustomer: CustomerItem | null;
    selectedSite: SiteItem | null;
    selectedFilter: FilterItem | null;
    selectedFilterDetail: FilterDetailData | null;
    selectedUser: UserItem | null;

    // Loading / error per domain
    customersLoading: boolean;
    customersError: string | null;
    sitesLoading: boolean;
    sitesError: string | null;
    filtersLoading: boolean;
    filtersError: string | null;
    filterDetailLoading: boolean;
    filterDetailError: string | null;
    usersLoading: boolean;
    usersError: string | null;
    rolesLoading: boolean;
    rolesError: string | null;

    // Mutation (POST/PUT/DELETE) loading
    mutationLoading: boolean;
    mutationError: string | null;
}

// ─── Session persistence for selections (survive page refresh) ──────────────

const SESSION_KEYS = {
    selectedCustomer: 'app_selectedCustomer',
    selectedSite: 'app_selectedSite',
    selectedFilter: 'app_selectedFilter',
} as const;

function loadFromSession<T>(key: string): T | null {
    try {
        const raw = sessionStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function saveToSession(key: string, value: unknown): void {
    try {
        if (value === null) {
            sessionStorage.removeItem(key);
        } else {
            sessionStorage.setItem(key, JSON.stringify(value));
        }
    } catch { /* ignore quota errors */ }
}

const initialState: AppState = {
    customers: [],
    sites: [],
    filters: [],
    users: [],
    roles: [],

    selectedCustomer: loadFromSession<CustomerItem>(SESSION_KEYS.selectedCustomer),
    selectedSite: loadFromSession<SiteItem>(SESSION_KEYS.selectedSite),
    selectedFilter: loadFromSession<FilterItem>(SESSION_KEYS.selectedFilter),
    selectedFilterDetail: null,
    selectedUser: null,

    customersLoading: false,
    customersError: null,
    sitesLoading: false,
    sitesError: null,
    filtersLoading: false,
    filtersError: null,
    filterDetailLoading: false,
    filterDetailError: null,
    usersLoading: false,
    usersError: null,
    rolesLoading: false,
    rolesError: null,

    mutationLoading: false,
    mutationError: null,
};

// ─── Helpers ────────────────────────────────────────────────────────────────────

const extractArray = (raw: any): any[] => {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.customers)) return raw.customers;
    if (Array.isArray(raw?.sites)) return raw.sites;
    if (Array.isArray(raw?.items)) return raw.items;
    if (Array.isArray(raw?.data)) return raw.data;
    return [];
};

// ─── Async Thunks ───────────────────────────────────────────────────────────────

export const fetchCustomers = createAsyncThunk('app/fetchCustomers', async (_, { rejectWithValue }) => {
    const response = await CustomerService.getCustomers();
    if (response.success) {
        const list = extractArray(response.data);
        return list.map((c: any): CustomerItem => ({
            id: String(c.CustomerId ?? c.customerId ?? c.id ?? ''),
            name: c.CustomerName ?? c.name ?? '',
            accountNumber: c.AccountNumber ?? c.accountNumber ?? '',
            address: c.Address ?? c.address ?? '',
            city: c.City ?? c.city ?? '',
            state: c.State ?? c.state ?? '',
            zipCode: c.ZipCode ?? c.zipCode ?? '',
            country: c.Country ?? c.country ?? '',
            sites: c.SiteCount ?? c.siteCount ?? c.sites ?? 0,
            administrator: c.Administrator ?? c.administrator,
            status: c.Status ?? c.status,
        }));
    }
    const errBody = response.errorResponse as any;
    return rejectWithValue(errBody?.errorDescription || response.data);
});

export const fetchSites = createAsyncThunk('app/fetchSites', async (customerId: string, { rejectWithValue }) => {
    const response = await CustomerService.getCustomerSites(customerId);
    if (response.success) {
        const list = extractArray(response.data);
        return list.map((s: any): SiteItem => {
            const attrs = s.custom_attributes ?? {};
            return {
                id: String(s.SiteId ?? s.siteId ?? s.site_id ?? s.id ?? ''),
                name: s.SiteName ?? s.site_name ?? s.name ?? '',
                address: attrs.WAS_Entity_Address ?? attrs.was_entity_address ?? s.Address ?? s.address ?? s.site_address ?? '',
                city: attrs.WAS_Entity_City ?? attrs.was_entity_city ?? s.City ?? s.city ?? s.site_city ?? '',
                state: attrs.WAS_Entity_State ?? attrs.was_entity_state ?? s.State ?? s.state ?? s.site_state ?? '',
                zipCode: attrs.WAS_Entity_ZipCode ?? attrs.was_entity_zipcode ?? s.ZipCode ?? s.zipCode ?? s.zip_code ?? '',
                country: attrs.WAS_Entity_Country ?? attrs.was_entity_country ?? s.Country ?? s.country ?? s.Region ?? s.region ?? '',
                sitesCount: s.SiteCount ?? s.siteCount ?? s.site_count ?? 0,
                administrator: s.Administrator ?? s.administrator ?? s.admin_name ?? '',
                administratorStatus: s.AdministratorStatus ?? s.administratorStatus ?? s.administrator_status,
                status: s.Status ?? s.status,
                subscriptionLevel: s.SubscriptionLevel ?? s.subscriptionLevel ?? s.subscription_level,
            };
        });
    }
    const errBody = response.errorResponse as any;
    return rejectWithValue(errBody?.errorDescription || response.data);
});

export const fetchFilters = createAsyncThunk(
    'app/fetchFilters',
    async ({ siteId, siteName }: { siteId: string; siteName: string }, { rejectWithValue }) => {
        const response = await CustomerService.getFiltersBySiteId(siteId);
        if (response.success) {
            const raw = response.data as any;
            const items = Array.isArray(raw) ? raw : (raw?.data ?? []);
            return items.map((f: any): FilterItem => {
                const sub = f.subscription_details;
                let status: FilterItem['status'] = 'Active';
                if (sub?.is_expired) status = 'Expired';
                else if (sub?.is_trail) status = 'Trial';
                else if (!f.is_active) status = 'Inactive';
                const createdDate = f.created_on
                    ? moment.unix(f.created_on).format('DD MMMM YYYY')
                    : '';
                return {
                    id: f.filter_id ?? f.id,
                    name: f.filter_name ?? f.serial_number ?? '',
                    site: siteName,
                    createdOn: createdDate,
                    createdBy: f.created_by ?? '',
                    status,
                    subscriptionLevel: sub?.subscription_level ?? 0,
                };
            });
        }
        const errBody = response.errorResponse as any;
        return rejectWithValue(errBody?.errorDescription || response.data);
    }
);

export const fetchFilterDetail = createAsyncThunk(
    'app/fetchFilterDetail',
    async (filterId: string, { rejectWithValue }) => {
        const response = await CustomerService.getFilterByFilterId(filterId);
        if (response.success) {
            const raw = response.data as any;
            const d = raw?.data ?? raw;
            const detail: FilterDetailData = {
                filter_name: d.filter_name ?? d.serial_number ?? '',
                serial_number: d.serial_number ?? '',
                filter_id: d.filter_id ?? d.id ?? '',
                site_id: d.site_id ?? '',
                subscription_details: d.subscription_details ?? {
                    subscription_level: 0, subscription_description: '', start_date: 0,
                    expiration_date: 0, issued_date: 0, is_trail: false,
                    subscription_remaining_days: 0, is_expired: false, subscription_status: 0,
                },
                filter_type: d.filter_type ?? 0,
                filter_technology: d.filter_technology ?? 0,
                model_type: d.model_type ?? 0,
                design_pressure_max: d.design_pressure_max ?? 0,
                design_temperature_max: d.design_temperature_max ?? 0,
                differential_pressure_max: d.differential_pressure_max ?? 0,
                air_pressure_max: d.air_pressure_max ?? 0,
                flowrate_max: d.flowrate_max ?? 0,
                components: Array.isArray(d.components) ? d.components : [],
                created_on: d.created_on ?? 0,
                created_by: d.created_by ?? '',
                updated_on: d.updated_on ?? 0,
                updated_by: d.updated_by ?? '',
                is_active: d.is_active ?? false,
            };
            return detail;
        }
        const errBody = response.errorResponse as any;
        return rejectWithValue(errBody?.errorDescription || (typeof response.data === 'string' ? response.data : 'Failed to load filter details'));
    }
);

export const createCustomerAsync = createAsyncThunk(
    'app/createCustomer',
    async (payload: CreateCustomerRequest, { rejectWithValue }) => {
        const response = await CustomerService.registerCustomer(payload);
        if (response.success) return response.data;
        const errBody = response.errorResponse as any;
        return rejectWithValue(errBody?.errorDescription || response.data);
    }
);

export const deleteCustomerAsync = createAsyncThunk(
    'app/deleteCustomer',
    async (customerId: string, { rejectWithValue }) => {
        const response = await CustomerService.deleteCustomer(customerId);
        if (response.success) return customerId;
        const errBody = response.errorResponse as any;
        return rejectWithValue(errBody?.errorDescription || response.data);
    }
);

export const updateCustomerAsync = createAsyncThunk(
    'app/updateCustomer',
    async ({ id, payload }: { id: string; payload: CreateCustomerRequest }, { rejectWithValue }) => {
        const response = await CustomerService.updateRegisterCustomer(id, payload);
        if (response.success) return response.data;
        const errBody = response.errorResponse as any;
        return rejectWithValue(errBody?.errorDescription || response.data);
    }
);

export const createSiteAsync = createAsyncThunk(
    'app/createSite',
    async (payload: CreateSiteRequest, { rejectWithValue }) => {
        const response = await CustomerService.addSite(payload);
        if (response.success) return response.data;
        const errBody = response.errorResponse as any;
        return rejectWithValue(errBody?.errorDescription || response.data);
    }
);

export const deleteSiteAsync = createAsyncThunk(
    'app/deleteSite',
    async (siteId: string, { rejectWithValue }) => {
        const response = await CustomerService.deleteSite(siteId);
        if (response.success) return siteId;
        const errBody = response.errorResponse as any;
        return rejectWithValue(errBody?.errorDescription || response.data);
    }
);

export const fetchRoles = createAsyncThunk('app/fetchRoles', async (_, { rejectWithValue }) => {
    const response = await CustomerService.getRoles();
    if (response.success) {
        const raw = response.data as any;
        const list = raw?.data?.roles ?? raw?.roles ?? (Array.isArray(raw) ? raw : []);
        return list.map((r: any): RoleItem => ({
            id: r.id ?? '',
            name: r.name ?? '',
        }));
    }
    const errBody = response.errorResponse as any;
    return rejectWithValue(errBody?.errorDescription || response.data);
});

// ─── Slice ──────────────────────────────────────────────────────────────────────

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        setSelectedCustomer(state, action: PayloadAction<CustomerItem | null>) {
            state.selectedCustomer = action.payload;
            saveToSession(SESSION_KEYS.selectedCustomer, action.payload);
        },
        setSelectedSite(state, action: PayloadAction<SiteItem | null>) {
            state.selectedSite = action.payload;
            saveToSession(SESSION_KEYS.selectedSite, action.payload);
        },
        setSelectedFilter(state, action: PayloadAction<FilterItem | null>) {
            state.selectedFilter = action.payload;
            saveToSession(SESSION_KEYS.selectedFilter, action.payload);
        },
        setSelectedFilterDetail(state, action: PayloadAction<FilterDetailData | null>) {
            state.selectedFilterDetail = action.payload;
        },
        setSelectedUser(state, action: PayloadAction<UserItem | null>) {
            state.selectedUser = action.payload;
        },
        setCustomers(state, action: PayloadAction<CustomerItem[]>) {
            state.customers = action.payload;
        },
        setSites(state, action: PayloadAction<SiteItem[]>) {
            state.sites = action.payload;
        },
        setFilters(state, action: PayloadAction<FilterItem[]>) {
            state.filters = action.payload;
        },
        setUsers(state, action: PayloadAction<UserItem[]>) {
            state.users = action.payload;
        },
        clearCustomersError(state) { state.customersError = null; },
        clearSitesError(state) { state.sitesError = null; },
        clearFiltersError(state) { state.filtersError = null; },
        clearFilterDetailError(state) { state.filterDetailError = null; },
        clearMutationError(state) { state.mutationError = null; },
    },
    extraReducers: (builder) => {
        builder
            // Customers
            .addCase(fetchCustomers.pending, (state) => { state.customersLoading = true; state.customersError = null; })
            .addCase(fetchCustomers.fulfilled, (state, action) => { state.customersLoading = false; state.customers = action.payload; })
            .addCase(fetchCustomers.rejected, (state, action) => { state.customersLoading = false; state.customersError = String(action.payload ?? 'Failed to fetch customers'); })
            // Sites
            .addCase(fetchSites.pending, (state) => { state.sitesLoading = true; state.sitesError = null; })
            .addCase(fetchSites.fulfilled, (state, action) => { state.sitesLoading = false; state.sites = action.payload; })
            .addCase(fetchSites.rejected, (state, action) => { state.sitesLoading = false; state.sitesError = String(action.payload ?? 'Failed to fetch sites'); })
            // Filters
            .addCase(fetchFilters.pending, (state) => { state.filtersLoading = true; state.filtersError = null; })
            .addCase(fetchFilters.fulfilled, (state, action) => { state.filtersLoading = false; state.filters = action.payload; })
            .addCase(fetchFilters.rejected, (state, action) => { state.filtersLoading = false; state.filtersError = String(action.payload ?? 'Failed to fetch filters'); })
            // Filter detail
            .addCase(fetchFilterDetail.pending, (state) => { state.filterDetailLoading = true; state.filterDetailError = null; })
            .addCase(fetchFilterDetail.fulfilled, (state, action) => { state.filterDetailLoading = false; state.selectedFilterDetail = action.payload; })
            .addCase(fetchFilterDetail.rejected, (state, action) => { state.filterDetailLoading = false; state.filterDetailError = String(action.payload ?? 'Failed to load filter details'); })
            // Create customer
            .addCase(createCustomerAsync.pending, (state) => { state.mutationLoading = true; state.mutationError = null; })
            .addCase(createCustomerAsync.fulfilled, (state) => { state.mutationLoading = false; })
            .addCase(createCustomerAsync.rejected, (state, action) => { state.mutationLoading = false; state.mutationError = String(action.payload ?? 'Failed to create customer'); })
            // Update customer
            .addCase(updateCustomerAsync.pending, (state) => { state.mutationLoading = true; state.mutationError = null; })
            .addCase(updateCustomerAsync.fulfilled, (state) => { state.mutationLoading = false; })
            .addCase(updateCustomerAsync.rejected, (state, action) => { state.mutationLoading = false; state.mutationError = String(action.payload ?? 'Failed to update customer'); })
            // Delete customer
            .addCase(deleteCustomerAsync.pending, (state) => { state.mutationLoading = true; state.mutationError = null; })
            .addCase(deleteCustomerAsync.fulfilled, (state, action) => {
                state.mutationLoading = false;
                state.customers = state.customers.filter(c => c.id !== action.payload);
                if (state.selectedCustomer?.id === action.payload) state.selectedCustomer = null;
            })
            .addCase(deleteCustomerAsync.rejected, (state, action) => { state.mutationLoading = false; state.mutationError = String(action.payload ?? 'Failed to delete customer'); })
            // Create site
            .addCase(createSiteAsync.pending, (state) => { state.mutationLoading = true; state.mutationError = null; })
            .addCase(createSiteAsync.fulfilled, (state) => { state.mutationLoading = false; })
            .addCase(createSiteAsync.rejected, (state, action) => { state.mutationLoading = false; state.mutationError = String(action.payload ?? 'Failed to create site'); })
            // Delete site
            .addCase(deleteSiteAsync.pending, (state) => { state.mutationLoading = true; state.mutationError = null; })
            .addCase(deleteSiteAsync.fulfilled, (state, action) => {
                state.mutationLoading = false;
                state.sites = state.sites.filter(s => s.id !== action.payload);
                if (state.selectedSite?.id === action.payload) state.selectedSite = null;
            })
            .addCase(deleteSiteAsync.rejected, (state, action) => { state.mutationLoading = false; state.mutationError = String(action.payload ?? 'Failed to delete site'); })
            // Roles
            .addCase(fetchRoles.pending, (state) => { state.rolesLoading = true; state.rolesError = null; })
            .addCase(fetchRoles.fulfilled, (state, action) => { state.rolesLoading = false; state.roles = action.payload; })
            .addCase(fetchRoles.rejected, (state, action) => { state.rolesLoading = false; state.rolesError = String(action.payload ?? 'Failed to fetch roles'); });
    },
});

export const {
    setSelectedCustomer,
    setSelectedSite,
    setSelectedFilter,
    setSelectedFilterDetail,
    setSelectedUser,
    setCustomers,
    setSites,
    setFilters,
    setUsers,
    clearCustomersError,
    clearSitesError,
    clearFiltersError,
    clearFilterDetailError,
    clearMutationError,
} = appSlice.actions;

export default appSlice.reducer;
